import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUserId } from '../store/userSlice';
import { UserManagementControllerService } from '../api/index.ts';
import { toast } from 'react-toastify';
import './UserManagement.css';

function UserManagement({ embedded }) {
  const sportsClubId = useSelector(selectUserId);

  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [groupedUsers, setGroupedUsers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvingUserId, setApprovingUserId] = useState(null);
  const [removingCoachKey, setRemovingCoachKey] = useState(null);

  // Modal potwierdzenia usunięcia trenera
  const [showRemoveCoachConfirm, setShowRemoveCoachConfirm] = useState(false);
  const [coachToRemove, setCoachToRemove] = useState(null); // { teamId, coachId, coachName }

  // Modal zmiany przypisania trenera
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignCoach, setReassignCoach] = useState(null); // { coachId, coachName, currentTeamIds }
  const [reassignSelectedTeamIds, setReassignSelectedTeamIds] = useState([]);
  const [reassignSaving, setReassignSaving] = useState(false);

  // Oczekujące zaproszenia
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [resendingEmail, setResendingEmail] = useState(null);

  // Modal zaproszenia trenerów
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState([]);
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!sportsClubId) {
        setError('Brak ID klubu sportowego');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError('');

        // Pobierz dane z nowego endpointa
        const [data, pending] = await Promise.all([
          UserManagementControllerService.getSportsClubUsers(sportsClubId),
          UserManagementControllerService.getPendingInvitations(sportsClubId).catch(() => [])
        ]);
        setPendingInvitations(pending);

        // Pobierz drużyny z węzła "teams"
        if (data.teams) {
          setTeams(data.teams);
        }

        // Pobierz pracowników (trenerów) z węzła "employees"
        const employees = data.employees || [];
        setUsers(employees);

        // Grupuj pracowników według teamIds (tablica)
        const grouped = {};
        employees.forEach(employee => {
          const teamIds = employee.teamIds || [];
          teamIds.forEach(teamId => {
            if (!grouped[teamId]) {
              grouped[teamId] = [];
            }
            grouped[teamId].push(employee);
          });
        });
        setGroupedUsers(grouped);

      } catch (error) {
        setError('Nie udało się pobrać danych: ' + (error.message || 'Nieznany błąd'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sportsClubId]);

  // Funkcja pomocnicza do formatowania nazwy drużyny
  const getTeamName = (team) => {
    if (!team) return 'Nieznana drużyna';

    const teamName = team.teamName || 'Drużyna';
    const teamTier = team.teamTier || '';

    if (teamTier) {
      return `${teamName} - ${teamTier}`;
    }

    return teamName;
  };

  // Sprawdź status akceptacji trenera dla konkretnej drużyny
  const isApprovedForTeam = (employee, teamId) => {
    const approval = (employee.teamApprovals || []).find(a => a.teamId === teamId);
    return approval?.isApproved ?? false;
  };

  // Odśwież dane z serwera
  const refreshData = async () => {
    const [data, pending] = await Promise.all([
      UserManagementControllerService.getSportsClubUsers(sportsClubId),
      UserManagementControllerService.getPendingInvitations(sportsClubId).catch(() => [])
    ]);
    setPendingInvitations(pending);

    if (data.teams) {
      setTeams(data.teams);
    }

    const employees = data.employees || [];
    setUsers(employees);

    const grouped = {};
    employees.forEach(employee => {
      const teamIds = employee.teamIds || [];
      teamIds.forEach(teamId => {
        if (!grouped[teamId]) {
          grouped[teamId] = [];
        }
        grouped[teamId].push(employee);
      });
    });
    setGroupedUsers(grouped);
  };

  // Funkcja do akceptowania trenera
  const handleApproveCoach = async (userId) => {
    try {
      setApprovingUserId(userId);
      await UserManagementControllerService.approveCoach(userId);
      await refreshData();
    } catch (error) {
      setError('Nie udało się zaakceptować trenera: ' + (error.message || 'Nieznany błąd'));
    } finally {
      setApprovingUserId(null);
    }
  };

  // Funkcja otwierająca modal potwierdzenia usunięcia trenera
  const handleRemoveCoach = (teamId, coachId, coachName) => {
    setCoachToRemove({ teamId, coachId, coachName });
    setShowRemoveCoachConfirm(true);
  };

  // Funkcja wykonująca usunięcie trenera z drużyny
  const confirmRemoveCoach = async () => {
    if (!coachToRemove) return;

    const { teamId, coachId } = coachToRemove;
    const key = `${teamId}-${coachId}`;
    try {
      setRemovingCoachKey(key);
      await UserManagementControllerService.removeCoachFromTeam(teamId, coachId);
      await refreshData();
      setShowRemoveCoachConfirm(false);
      setCoachToRemove(null);
      toast.success('Trener został usunięty z drużyny');
    } catch (error) {
      setError('Nie udało się usunąć trenera z drużyny: ' + (error.message || 'Nieznany błąd'));
    } finally {
      setRemovingCoachKey(null);
    }
  };

  // Funkcje dla zaproszenia trenerów
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddInviteEmail = () => {
    const trimmed = inviteEmailInput.trim().toLowerCase();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setInviteError('Podaj poprawny adres email');
      return;
    }
    if (inviteEmails.includes(trimmed)) {
      setInviteError('Ten email jest już na liście');
      return;
    }
    setInviteEmails(prev => [...prev, trimmed]);
    setInviteEmailInput('');
    setInviteError('');
  };

  const handleRemoveInviteEmail = (emailToRemove) => {
    setInviteEmails(prev => prev.filter(e => e !== emailToRemove));
  };

  const handleSendInvitations = async () => {
    if (inviteEmails.length === 0) return;
    try {
      setInviteSending(true);
      setInviteError('');
      await UserManagementControllerService.sendInvitations(sportsClubId, inviteEmails);
      setInviteSuccess('Zaproszenia zostały wysłane!');
      setInviteEmails([]);
      await refreshData();
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess('');
      }, 2000);
    } catch (err) {
      setInviteError('Nie udało się wysłać zaproszeń: ' + (err.message || 'Nieznany błąd'));
    } finally {
      setInviteSending(false);
    }
  };

  const openInviteModal = () => {
    setShowInviteModal(true);
    setInviteEmails([]);
    setInviteEmailInput('');
    setInviteError('');
    setInviteSuccess('');
  };

  const handleResendInvitation = async (email) => {
    try {
      setResendingEmail(email);
      await UserManagementControllerService.resendInvitation(sportsClubId, email);
      await refreshData();
    } catch (err) {
      setError('Nie udało się ponownie wysłać zaproszenia: ' + (err.message || 'Nieznany błąd'));
    } finally {
      setResendingEmail(null);
    }
  };

  const handleOpenReassignModal = (user) => {
    setReassignCoach({
      coachId: user.id,
      coachName: `${user.firstName} ${user.lastName}`,
      currentTeamIds: user.teamIds || []
    });
    setReassignSelectedTeamIds(user.teamIds || []);
    setShowReassignModal(true);
  };

  const handleReassignToggleTeam = (teamId) => {
    setReassignSelectedTeamIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const handleReassignConfirm = async () => {
    if (!reassignCoach || reassignSelectedTeamIds.length === 0) return;
    try {
      setReassignSaving(true);
      await UserManagementControllerService.reassignCoach(reassignCoach.coachId, reassignSelectedTeamIds);
      await refreshData();
      setShowReassignModal(false);
      setReassignCoach(null);
      toast.success('Przypisanie trenera zostało zaktualizowane');
    } catch (error) {
      setError('Nie udało się zmienić przypisania: ' + (error?.body?.message || error.message || 'Nieznany błąd'));
    } finally {
      setReassignSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="user-management-container">
        <div className="loading-message">Ładowanie danych...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      {!embedded && <h2 className="page-title">Zarządzanie użytkownikami</h2>}
      <div className="user-management-header">
        <button
          type="button"
          className="invite-button"
          onClick={openInviteModal}
        >
          Zaproś trenerów
        </button>
      </div>

      {pendingInvitations.length > 0 && (
        <div className="pending-invitations-section">
          <div className="pending-invitations-header">
            <span className="pending-invitations-icon">📧</span>
            <h2>Oczekujące zaproszenia</h2>
            <span className="pending-invitations-count">{pendingInvitations.length}</span>
          </div>
          <div className="pending-invitations-list">
            {pendingInvitations.map((inv) => {
              const hasFailed = !inv.emailSent && inv.retryCount > 0 && inv.lastError;
              const isSending = !inv.emailSent && inv.retryCount === 0;
              return (
                <div key={inv.email} className={`pending-invitation-item${hasFailed ? ' invitation-failed' : ''}`}>
                  <div className="pending-invitation-info">
                    <span className="pending-invitation-email">{inv.email}</span>
                    {inv.emailSent && (
                      <span className="pending-invitation-status invitation-status-sent">Wysłano, oczekuje na rejestrację</span>
                    )}
                    {isSending && (
                      <span className="pending-invitation-status invitation-status-sending">Wysyłanie...</span>
                    )}
                    {hasFailed && (
                      <span className="pending-invitation-status invitation-status-error">
                        Błąd wysyłki (próba {inv.retryCount}/3)
                      </span>
                    )}
                  </div>
                  {hasFailed && (
                    <button
                      className="resend-invitation-btn"
                      onClick={() => handleResendInvitation(inv.email)}
                      disabled={resendingEmail === inv.email}
                    >
                      {resendingEmail === inv.email ? 'Wysyłanie...' : 'Wyślij ponownie'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {teams.length === 0 ? (
        <div className="no-teams-message">
          <p>Brak drużyn w klubie. Dodaj drużyny, aby móc zarządzać trenerami.</p>
        </div>
      ) : (
        <div className="teams-sections">
          {teams.map((team, index) => {
            const teamUsers = groupedUsers[team.id] || [];

            return (
              <div key={team.id || index} className="team-section">
                <div className="team-section-header">
                  <h2>{getTeamName(team)}</h2>
                  <span className="team-users-count">
                    {teamUsers.length} {teamUsers.length === 1 ? 'trener' : 'trenerów'}
                  </span>
                </div>

                {teamUsers.length === 0 ? (
                  <div className="no-users-message">
                    Brak trenerów przypisanych do tej drużyny
                  </div>
                ) : (
                  <div className="users-grid">
                    {teamUsers.map(user => {
                      const approved = isApprovedForTeam(user, team.id);
                      return (
                        <div key={user.id} className={`user-card ${!approved ? 'pending-approval' : ''}`}>
                          <div className="user-icon">👤</div>
                          <div className="user-info">
                            <h3>{user.firstName} {user.lastName}</h3>
                            {approved ? (
                              <span className="status-badge approved">Zaakceptowany</span>
                            ) : (
                              <span className="status-badge pending">Oczekuje akceptacji</span>
                            )}
                          </div>
                          {!approved && (
                            <button
                              className="approve-button"
                              onClick={() => handleApproveCoach(user.id)}
                              disabled={approvingUserId === user.id}
                            >
                              {approvingUserId === user.id ? 'Akceptowanie...' : 'Akceptuj'}
                            </button>
                          )}
                          {approved && (
                            <div className="coach-action-buttons">
                              <button
                                className="reassign-button"
                                onClick={() => handleOpenReassignModal(user)}
                              >
                                Zmień przypisanie
                              </button>
                              <button
                                className="remove-button"
                                onClick={() => handleRemoveCoach(team.id, user.id, `${user.firstName} ${user.lastName}`)}
                                disabled={removingCoachKey === `${team.id}-${user.id}`}
                              >
                                {removingCoachKey === `${team.id}-${user.id}` ? 'Usuwanie...' : 'Usuń'}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Trenerzy bez przypisanej drużyny (teamIds puste lub nie pasują) */}
      {users.some(user => !user.teamIds || user.teamIds.length === 0 || !user.teamIds.some(tid => teams.find(t => t.id === tid))) && (
        <div className="team-section unassigned-section">
          <div className="team-section-header">
            <h2>Trenerzy bez przypisanej drużyny</h2>
            <span className="team-users-count">
              {users.filter(user => !user.teamIds || user.teamIds.length === 0 || !user.teamIds.some(tid => teams.find(t => t.id === tid))).length}
            </span>
          </div>
          <div className="users-grid">
            {users
              .filter(user => !user.teamIds || user.teamIds.length === 0 || !user.teamIds.some(tid => teams.find(t => t.id === tid)))
              .map(user => (
                <div key={user.id} className="user-card pending-approval">
                  <div className="user-icon">👤</div>
                  <div className="user-info">
                    <h3>{user.firstName} {user.lastName}</h3>
                    <span className="status-badge pending">Nieprzypisany do drużyny</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia trenera */}
      {showRemoveCoachConfirm && coachToRemove && (
        <div className="modal-overlay" onClick={() => { setShowRemoveCoachConfirm(false); setCoachToRemove(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Potwierdź usunięcie trenera</h2>
            <p className="delete-confirm-message">
              Czy na pewno chcesz usunąć trenera <strong>{coachToRemove.coachName}</strong> z tej drużyny?
            </p>
            <p className="delete-warning">Trener straci dostęp do tej drużyny.</p>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => { setShowRemoveCoachConfirm(false); setCoachToRemove(null); }}
                className="btn btn-secondary"
                disabled={removingCoachKey === `${coachToRemove.teamId}-${coachToRemove.coachId}`}
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={confirmRemoveCoach}
                className="btn btn-danger"
                disabled={removingCoachKey === `${coachToRemove.teamId}-${coachToRemove.coachId}`}
              >
                {removingCoachKey === `${coachToRemove.teamId}-${coachToRemove.coachId}` ? 'Usuwanie...' : 'Usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal zaproszenia trenerów */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-content invite-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Zaproś trenerów</h2>
            <p className="invite-description">
              Dodaj adresy email trenerów, których chcesz zaprosić do klubu.
              Zaproszony trener wskaże w procesie rejestracji grupę, którą prowadzi.
            </p>

            {inviteSuccess && (
              <div className="invite-success-message">{inviteSuccess}</div>
            )}

            {!inviteSuccess && (
              <>
                <div className="invite-input-row">
                  <input
                    type="email"
                    value={inviteEmailInput}
                    onChange={(e) => { setInviteEmailInput(e.target.value); setInviteError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddInviteEmail(); } }}
                    placeholder="Adres email trenera"
                    className="invite-email-input"
                    maxLength={100}
                  />
                  <button
                    type="button"
                    onClick={handleAddInviteEmail}
                    className="btn btn-secondary invite-add-btn"
                  >
                    Dodaj
                  </button>
                </div>

                {inviteError && <div className="invite-error">{inviteError}</div>}

                {inviteEmails.length > 0 && (
                  <div className="invite-emails-list">
                    {inviteEmails.map((email) => (
                      <div key={email} className="invite-email-item">
                        <span className="invite-email-text">{email}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveInviteEmail(email)}
                          className="invite-email-remove"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="btn btn-secondary"
                    disabled={inviteSending}
                  >
                    Anuluj
                  </button>
                  <button
                    type="button"
                    onClick={handleSendInvitations}
                    className="btn btn-primary"
                    disabled={inviteSending || inviteEmails.length === 0}
                  >
                    {inviteSending ? 'Wysyłanie...' : 'Wyślij'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Modal zmiany przypisania trenera */}
      {showReassignModal && reassignCoach && (
        <div className="modal-overlay" onClick={() => { setShowReassignModal(false); setReassignCoach(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Zmień przypisanie</h2>
            <p className="reassign-description">
              Wybierz drużyny dla trenera <strong>{reassignCoach.coachName}</strong>:
            </p>
            <div className="reassign-teams-list">
              {teams.map(team => (
                <label key={team.id} className="reassign-team-item">
                  <input
                    type="checkbox"
                    checked={reassignSelectedTeamIds.includes(team.id)}
                    onChange={() => handleReassignToggleTeam(team.id)}
                  />
                  <span>{getTeamName(team)}</span>
                </label>
              ))}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => { setShowReassignModal(false); setReassignCoach(null); }}
                className="btn btn-secondary"
                disabled={reassignSaving}
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={handleReassignConfirm}
                className="btn btn-primary"
                disabled={reassignSaving || reassignSelectedTeamIds.length === 0}
              >
                {reassignSaving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
